import fuzzy from 'fuzzy';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {boxedInputColorReaders} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {BoxedInputIcon} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputIcon';
import {BoxedInputStatic} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputStatic';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckmark} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckmark';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownItemIcon} from '../../../../common/dropdowns/dropdownItemIcon';
import {DeprecatedSearchDropdown} from '../../../../common/dropdowns/search/deprecatedSearchDropdown';
import {SyncDropdownSource} from '../../../../common/dropdowns/source/syncDropdownSource';
import {
  InboxesSearchQueryBuilderInputValue,
  InboxesSearchQueryBuilderSyncDropdownInputConfig,
} from './inboxesSearchQueryBuilderTypes';

/*
 * Props.
 */

interface InboxesSearchQueryBuilderSyncDropdownInputProps
  extends InboxesSearchQueryBuilderSyncDropdownInputConfig,
    InboxesSearchQueryBuilderInputValue {}

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
  justify-self: start;
  max-width: 100%;
`;

const StyledPlaceholderSpan = styled.span`
  color: ${boxedInputColorReaders.textPlaceholder};
`;

/*
 * Component.
 */

export const InboxesSearchQueryBuilderSyncDropdownInput: FC<
  InboxesSearchQueryBuilderSyncDropdownInputProps
> = (props) => {
  const theme = useUserTheme();
  const {icon, placeholder, items, inputValue, onChange, modifier} = props;
  const {value} = inputValue;

  const dropdownSource = useMemo(
    () =>
      new SyncDropdownSource({
        items,
        rowFilter: (itemsToFilter, query) =>
          itemsToFilter.filter((item) => fuzzy.match(query, item.inputValue.value, {caseSensitive: false})),
        renderRow: (item) => {
          const isChecked = value === item.inputValue.value;

          return (
            <DropdownItem
              onSelect={() =>
                onChange({
                  ...inputValue,
                  ...item.inputValue,
                  modifier,
                })
              }
            >
              <DropdownItemIcon iconName={item.icon} iconSize={16} label={item.label} />
              <DropdownItemContent isChecked={isChecked}>{item.label}</DropdownItemContent>
              <DropdownItemCheckmark isChecked={isChecked} />
            </DropdownItem>
          );
        },
      }),
    [inputValue, items, modifier, onChange, value],
  );

  const renderButton = (isOpen: boolean) => {
    const selectedItem = items.find((i) => i.inputValue.value === value);

    const displayedIcon = selectedItem?.icon || icon;
    const displayedValue = selectedItem?.label || (value.length > 0 ? value : undefined);

    return (
      <BoxedInput size={VisualSizesEnum.MEDIUM} noMargin isButton hasHover={isOpen}>
        {(childProps) => (
          <>
            <BoxedInputIcon {...childProps} iconName={displayedIcon} color={theme.greys.shade70} />
            <BoxedInputStatic
              {...childProps}
              noMarginLeft
              value={displayedValue || <StyledPlaceholderSpan>{placeholder}</StyledPlaceholderSpan>}
            />
            <BoxedInputIcon {...childProps} iconName="chevronDownSmall" />
          </>
        )}
      </BoxedInput>
    );
  };

  return (
    <StyledDropdownBoxCoordinator
      renderButton={renderButton}
      renderDropdown={(popoverProps) => (
        <DeprecatedSearchDropdown {...popoverProps} title={props.title} source={dropdownSource} />
      )}
    />
  );
};
